/**
 * `cart_summary` page-load trigger — WAT-11358.
 *
 * Stays in Upper Funnel (the mapper and emitter live in
 * @directv-digital/dds.services.analytics-service): it owns the app-specific
 * bits — reading the shop-written localStorage cart through the same helper the
 * header cart icon uses, and the once-per-page-load guard from AC1.
 *
 * Callers are the page-load analytics blocks of pages/index.tsx,
 * pages/recognized/index.tsx, pages/uf-sandbox/index.tsx and both
 * pages/tools/*signal-strength on the Pages Router, plus
 * page-bootstrap-client.tsx on the App Router.
 *
 * Call this on EVERY page load, including routes that display no cart, and say
 * so via `cartIsDisplayed`. Skipping the call instead would leave the dedupe key
 * on the previous page — see the guard below.
 */

import { getSavedCart } from 'src/utils/UtilityFunctions';
import {
  buildCartSummaryPayload,
  fireCartSummary
} from '@directv-digital/dds.services.analytics-service';

interface ITrackCartSummaryOptions {
  /**
   * Whether this page actually shows a cart. Callers derive it from the same
   * props that hide the header cart at their render site — `disableGNav`,
   * `hideCartIcon`, a modal page's `hideGN`, or the App Router layout's
   * equivalents. A cart persisted in localStorage must not report against a
   * page the user sees no cart on.
   *
   * Defaults to true: the CMS content routes are the overwhelming majority of
   * callers, and a page that stops reporting is invisible in analytics whereas
   * an unexpected one shows up as a page dimension nobody recognises.
   */
  cartIsDisplayed?: boolean;
  /**
   * Dedupe key. Defaults to the current pathname — pass an explicit value when
   * the caller knows the logical page (tests, or a route whose pathname is
   * shared).
   */
  pageKey?: string;
  /**
   * `svodsData` from the genre cart CMS story
   * (`cdn/stories/sales/streamtv/global/genrecart`), keyed by offer code with
   * hyphens swapped for underscores — the same lookup the shared cart drawer
   * uses to label a genre pack's included SVODs.
   *
   * The cart stores only `{ offerId, offerCode }` for those rows, so without a
   * name the mapper omits them entirely.
   *
   * Supplied by the caller from the server-fetched genre cart story, cached with
   * the other global stories (`CACHE_KEYS.globalStoryblok.genrecart`) and surfaced
   * as `cartSvodsData` by the cache repositories.
   *
   * This module deliberately does NOT fetch it. An earlier revision did, and the
   * await delayed the only cart_summary emit by up to the fetch's ceiling — losing
   * the event outright if the user navigated first, since nothing had been queued
   * for the pagehide flush to rescue. Reporting a cart must not depend on a network
   * round trip.
   *
   * Omitting it is safe: the mapper reads an absent value as "do not report the
   * SVOD rows", which is exactly the pre-SVOD payload.
   */
  svodsData?: Record<string, any>;
}

/**
 * AC1: fire once per page load, not on every cart mutation.
 *
 * A single slot rather than a set of every page seen. It suppresses repeat calls
 * for the SAME page — re-renders, StrictMode's double-invoked effects, a second
 * idle callback — while a client-side route change to a different page fires
 * again.
 *
 * The slot advances on every page load that reaches this module, whether or not
 * an event goes out. That is why cart-less routes must still call in rather than
 * skip: a route that never touches the key would leave it pointing at the
 * previous page, and a genuine A -> cart-less route -> A navigation would be
 * swallowed as if it were a re-render of A. Once the middle page is silent the
 * two cases are indistinguishable from here, so the caller cannot compensate.
 */
let lastSeenKey: string | null = null;

/** Test seam — clears the once-per-page-load guard. */
export const resetCartSummaryDedupe = (): void => {
  lastSeenKey = null;
};

/**
 * Reads the latest cart and fires `cart_summary` for it.
 *
 * Synchronous on purpose. Everything it needs is already in hand — the cart from
 * localStorage, the SVOD names from props — so the event is queued in the same
 * task the caller runs in and cannot be lost to a navigation mid-await.
 *
 * @returns `true` when the event fired; `false` when suppressed — server-side,
 * already handled for this page, no cart displayed, or an empty cart.
 */
export const trackCartSummary = (
  options: ITrackCartSummaryOptions = {}
): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const pageKey = options.pageKey ?? window.location?.pathname ?? '';
  if (pageKey === lastSeenKey) {
    return false;
  }
  // Claim the slot before any early return below, so every page load counts.
  lastSeenKey = pageKey;

  if (options.cartIsDisplayed === false) {
    return false;
  }

  const cart = getSavedCart();

  // Both calls below are third-party. Caught so that reporting a cart can never
  // degrade the page — the same guarantee the rest of this flow makes.
  try {
    // The mapper is synchronous (its return value decides whether anything fires),
    // while fireCartSummary rides the post-paint analytics queue and returns void.
    // So the empty-cart decision (AC6) has to happen here, not be inferred from a
    // return value the queue would have discarded.
    const payload = buildCartSummaryPayload(cart, { svodsData: options.svodsData });
    if (!payload) {
      return false;
    }

    fireCartSummary(payload);
    return true;
  } catch {
    // Silent: analytics must not surface a console error. The dedupe key is already
    // claimed above, so a throw cannot cause a retry storm on re-render.
    return false;
  }
};
